import { useEffect, useState } from 'react';
import {
    Box,
    Button,
    IconButton,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Text,
} from '@chakra-ui/react';
import { WarningIcon, SpinnerIcon } from '@chakra-ui/icons';
import { FaIcon } from '@acp/core-ui';
import { faCloudUpload } from '@fortawesome/pro-light-svg-icons';
import { useFormContext } from 'react-hook-form';
import { propertySchema } from '@acp/shared';
import z from 'zod';
import { TemporaryDocumentUploadCompliance } from './temporary-document-uploaded';
import { GenericUploadQueueContext } from '../../document/components/generic-uploader/context';

interface StatusIconProps {
    status: string;
    fileCount: number;
}

enum UploadStatus {
    NONE = 'NONE',
    SUCCESS = 'SUCCESS',
    FAILED = 'FAILED',
    PROCESSING = 'PROCESSING'
}

function StatusIcon({ status, fileCount }: StatusIconProps) {
    const iconProps = {
        size: 'xs',
        variant: 'ghost',
        'aria-label': `${status} icon`,
        top: '-10px',
        right: '-10px',
        borderRadius: 'full',
        zIndex: '1',
        _hover: { transform: 'none', cursor: 'default' },
        _active: { transform: 'none' },
    };

    switch (status) {
        case UploadStatus.SUCCESS:
            return (
                <Box
                    position="absolute"
                    top="-10px"
                    left="25px"
                    w="25px"
                    h="25px"
                    zIndex="1"
                    borderRadius="full"
                    bg="green.600"
                    color="green.600"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    hidden={fileCount === 0}
                >
                    <Text fontSize="sm" fontWeight="bold" color="white">
                        {fileCount}
                    </Text>
                </Box>
            );
        case UploadStatus.FAILED:
            return <IconButton {...iconProps} icon={<WarningIcon />} bg="red.500" color="white" position="absolute" hidden={fileCount === 0} />;
        case UploadStatus.PROCESSING:
            return <IconButton {...iconProps} icon={<SpinnerIcon />} bg="gray.500" color="white" position="absolute" hidden={fileCount === 0} />;
        default:
            return null;
    }
}

interface FileButtonProps {
    fileCount: number;
    uploadStatus: string;
    onButtonClick: () => void;
}
function FileButton({ fileCount, uploadStatus, onButtonClick }: FileButtonProps) {
    const handleButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onButtonClick();
    };
    return (
        <Button
            position="relative"
            w="40px"
            h="40px"
            borderRadius="md"
            bg="white"
            variant="solid"
            border="1px dashed #E2E8F0"
            _hover={{
                bg: 'white',
                cursor: 'default',
                '& > svg': {
                    opacity: 0.5,
                    transition: 'opacity 0.3s ease-in-out',
                },
            }}
            onClick={handleButtonClick}
        >
            <StatusIcon status={uploadStatus} fileCount={fileCount} />
            <FaIcon icon={faCloudUpload} size="lg" color="orange" />
        </Button>
    );
}

/**
 * The main component for handling file uploads and displaying a modal.
 */
interface FileInputProps {
    portfolioPropertyId: number;
}
export function FileInputWithCounter({ portfolioPropertyId }: FileInputProps) {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [uploadMetrics, setUploadMetrics] = useState({
        allUploadCount: 0,
        successCount: 0,
        isProcessing: false,
        hasFailures: false,
    });
    const fieldPath = `properties.${portfolioPropertyId}` as const;
    const machine = GenericUploadQueueContext.useActorRef();
    const form = useFormContext();
    const property = form.getValues(fieldPath);
    useEffect(() => {
        const subscription = machine.subscribe((state) => {
            const { queue } = state.context;

            const allUploads = queue.length;
            const successUploads = queue.filter((actor) => actor.getSnapshot().matches('success')).length;
            const failuresExist = queue.some((actor) => actor.getSnapshot().matches('failed'));
            const isProcessing = queue.some((actor) => actor.getSnapshot().matches('uploading') || actor.getSnapshot().matches('queued'));

            setUploadMetrics({
                allUploadCount: allUploads,
                successCount: successUploads,
                isProcessing,
                hasFailures: failuresExist,
            });
        });
        const propertyData = propertySchema.extend({
            isUploading: z.boolean().optional().default(true),
        }).safeParse(property);

        if (propertyData.data !== undefined && !propertyData.data.isUploading) {
            const documentCount = propertyData.data.documents.smoke.length
            + propertyData.data.documents.gas.length
            + propertyData.data.documents.electrical.length;
            if (documentCount === 0) {
                machine.send({ type: 'CLEAR_COMPLETED' });
            }
        }

        return () => subscription.unsubscribe();
    }, [machine, property]);

    const getUploadStatus = () => {
        const {
            allUploadCount,
            successCount,
            isProcessing,
            hasFailures,
        } = uploadMetrics;

        if (allUploadCount === 0) {
            return UploadStatus.NONE;
        }

        if (hasFailures) {
            return UploadStatus.FAILED;
        }

        if (isProcessing) {
            return UploadStatus.PROCESSING;
        }

        if (successCount === allUploadCount) {
            return UploadStatus.SUCCESS;
        }

        return UploadStatus.NONE;
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const uploadStatus = getUploadStatus();

    return (
        <>
            <FileButton
                fileCount={uploadMetrics.allUploadCount}
                uploadStatus={uploadStatus}
                onButtonClick={handleOpenModal}
            />
            <Modal isOpen={isModalOpen} onClose={handleCloseModal} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Uploaded Documents</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <TemporaryDocumentUploadCompliance />
                    </ModalBody>
                </ModalContent>
            </Modal>
        </>
    );
}
